import { useMutation, useQuery } from "@apollo/client"
import { GET_PRESET_PLANS, GET_PATIENT_CASES, GET_HEALTH_COACH_VISIT_SUMMARIES, GET_HEALTH_COACH_VISIT_SUMMARIES_ALL } from "../../../graphql/queries"
import { IGetPresetPlansOutput, IGetPresetPlansInput } from "../../../graphql/types/queries/presetPlans"
import { useEffect, useState } from "react"
import { Box, Button, Card, Flex, Heading, Text } from "rebass/styled-components"
import { IGetPatientCasesOutput, IGetPatientCasesInput, IPatientCase } from "../../../graphql/types/queries/patientCases"
import { EnhancedModal } from "../../../components/EnhancedModal"
import { IStepperStep, Stepper } from "../../../components/stepper/Steps"
import { Formik, FormikHelpers } from "formik"
import WellnessPlanTextEditor from "../components/WellnessPlanTextEditor"
import PleaseWait from "../../../components/PleaseWait"
import { UPSERT_HEALTH_COACH_VISIT_SUMMARY } from "../../../graphql/mutations"
import { IUpsertHealthcoachVisitSummaryInput, IUpsertHealthcoachVisitSummaryOutput } from "../../../graphql/types/mutations/healthcoaching"
import { IGetHealthCoachVisitSummariesForVisitInput, IGetHealthCoachVisitSummariesForVisitOutput } from "../../../graphql/types/queries/healthCoaching"
import { PatientCaseWithParams } from "../PatientCase"

interface IVisitSummaryProps {
  calendlyUri: string
  athenaId: string
  userId: string
  initialData?: {
    id: string | null | undefined
    selectedLinkedCourse: string | null | undefined
    selectedLinkedNote: string | null | undefined
    summary: string | undefined
    internalNotes: string | undefined
  } | undefined | null
  onClose: () => void
}
interface IValues {
  selectedLinkedCourse: { value: string, label: string } | null
  selectedLinkedNote: IPatientCase | null
  summary: string
  internalNotes: string
}

const SelectLinkedCourse: React.FC<{
  courses: Array<{
    value: string, label: string
  }>
  selectedCourse: {
    value: string, label: string
  } | null,
  onSelected: (e: {
    value: string, label: string
  }) => void
  isLoading: boolean
  skip: () => void
}> = ({ courses, selectedCourse, onSelected, skip, isLoading }) => {
  const [placeholders, setPlaceholders] = useState<Array<number>>([])
  useEffect(() => {
    if (courses.length % 3 === 0) return
    const res = []
    for (let index = 0; index < courses.length % 3; index++) {
      res.push(index)
    }
    setPlaceholders(res)
  }, [])
  return (
    <Flex flexDirection={'column'} overflowY={'hidden'} flex={1}>
      <Text fontSize={'20px'} mb={2} fontWeight={500}>Linked Course</Text>
      <Text fontWeight={300} mb={4} fontSize={'14px'}>Linking a course will allow the patient to view the summary on the course screen</Text>

      {!!isLoading ? (
        <Flex flex={1} justifyContent={'center'} alignItems={'center'}>
          <PleaseWait />
        </Flex>
      ) : (
        <>
          <Flex flexWrap={'wrap'} overflowY={'auto'}>
            {courses.map(x => {
              return (
                <Card mr='12px' p='8px 26px' justifyContent={'center'} alignItems={'center'} style={{
                  flex: 1, flexBasis: 'calc(33% - 12px)', display: 'flex',
                  background: selectedCourse && selectedCourse.value === x.value ? '#628fcb' : '',
                  cursor: 'pointer'
                }} mb={3} onClick={() => onSelected(x)}>
                  <Text style={{
                    color: selectedCourse && selectedCourse.value === x.value ? 'white' : ''
                  }} fontSize={'14px'} textAlign={'center'}>{x.label}</Text>
                </Card>
              )
            })}
            {placeholders.map(x => {
              return (
                <Flex mr='12px' p='8px 26px' justifyContent={'center'} alignItems={'center'} style={{ flex: 1, flexBasis: 'calc(33% - 12px)', display: 'flex' }} mb={3} />
              )
            })}
          </Flex>

          <Flex flex={1} />
        </>
      )}

      <Flex justifyContent={'flex-end'} alignItems={'center'} mt={4}>
        <Button ml={3} variant={'primary'} style={{ width: 'fit-content', padding: '6px 18px', fontSize: '16px' }} onClick={skip}>{!selectedCourse ? 'Do not link with a course' : 'Next'}</Button>
      </Flex>
    </Flex>
  )
}

const SelectLinkedNote: React.FC<{
  onChange: (e: IPatientCase) => void,
  cases: Array<IPatientCase>,
  selectedCase: IPatientCase | null,
  onClose: () => void
}> = ({ onChange, cases, selectedCase, onClose }) => {

  return (
    <Flex flexDirection={'column'} overflowY={'hidden'}>
      <Text fontWeight={500} mb={4}>Select Patient Case To Link</Text>
      <Flex flexDirection={'column'} overflowY={'auto'} flex={1}>
        {cases.filter(x => x.hcFilledFormId).map(x => (
          <Card mb={3} style={{
            background: selectedCase && selectedCase.id === x.id ? '#628fcb' : '',
            padding: '8px 16px',
            cursor: 'pointer'
          }} onClick={() => onChange(x)}>
            <Flex paddingX={'12px'} alignItems={'center'}>
              <Flex flexDirection={'column'}>
                <Text color={selectedCase && selectedCase.id === x.id ? 'white' : ''} fontWeight={500} fontSize={'14px'}>{x.subject}</Text>

                <Flex>
                  <Text color={selectedCase && selectedCase.id === x.id ? 'white' : ''} fontWeight={300} mr={2} fontSize={'12px'}>{`${x.lastModifiedDate}, `}</Text>
                  <Text color={selectedCase && selectedCase.id === x.id ? 'white' : ''} fontWeight={300} fontSize={'12px'}>{`${(x.documentSource || '').toLowerCase()}`}</Text>
                </Flex>
              </Flex>
            </Flex>
          </Card>
        ))}
      </Flex>

      <Button type='button' variant={'outline'} style={{ width: 'fit-content', padding: '4px 16px' }} alignSelf={'center'} mt={4} onClick={onClose}>Cancel</Button>
    </Flex>
  )
}

const Review: React.FC<{
  data: IValues
  onGoToStep: (stepNumber: number) => void
  save: () => void
  goBack: () => void
  disabled: boolean
}> = ({ data, onGoToStep, save, goBack, disabled }) => {
  return (
    <Flex flexDirection={'column'} overflowY={'hidden'} flex={1}>

      <Flex justifyContent={'space-between'} alignItems={'center'} mb={4}>
        <Text fontWeight={500} >Review</Text>
      </Flex>
      <Flex flexDirection={'column'} overflowY={'auto'} mb={4}>
        <Flex justifyContent={'space-between'} alignItems={'center'} mb={2}>
          <Text fontWeight={500} fontSize={'12px'}>Linked Course</Text>
          <Button variant={'outline'} style={{ width: 'fit-content', height: 'fit-content', padding: '2px 8px' }} fontSize={'12px'} onClick={() => onGoToStep(0)}>edit</Button>
        </Flex>
        <Box ml={4}>
          {data.selectedLinkedCourse ? (
            <Card mr='12px' p='8px 26px' justifyContent={'center'} alignItems={'center'} style={{
              flex: 1, flexBasis: 'calc(33% - 12px)', display: 'flex',
              background: '#628fcb',
            }}>
              <Text style={{
                color: 'white'
              }} fontSize={'12px'} textAlign={'center'}>{data.selectedLinkedCourse.label}</Text>
            </Card>
          ) : (
            <Text style={{ fontStyle: 'italic', fontWeight: 300 }} fontSize={'12px'}>No Course Linked</Text>
          )}
        </Box>
      </Flex>

      <Flex flexDirection={'column'} overflowY={'auto'} mb={4}>
        <Flex justifyContent={'space-between'} alignItems={'center'} mb={2}>
          <Text fontWeight={500} fontSize={'12px'}>Linked Patient Case</Text>
          <Button variant={'outline'} style={{ width: 'fit-content', height: 'fit-content', padding: '2px 8px' }} fontSize={'12px'} onClick={() => onGoToStep(1)}>edit</Button>
        </Flex>
        <Box ml={4}>
          {data.selectedLinkedNote ? (
            <Card mr='12px' p='8px 26px' justifyContent={'center'} alignItems={'center'} style={{
              flex: 1, flexBasis: 'calc(33% - 12px)', display: 'flex',
              background: '#628fcb',
            }}>
              <Flex flexDirection={'column'}>
                <Text color={'white'} fontWeight={500} fontSize={'12px'}>{data.selectedLinkedNote.subject}</Text>

                <Flex>
                  <Text color={'white'} fontWeight={300} mr={2} fontSize={'11px'}>{`${data.selectedLinkedNote.lastModifiedDate}, `}</Text>
                  <Text color={'white'} fontWeight={300} fontSize={'11px'}>{`${(data.selectedLinkedNote.documentSource || '').toLowerCase()}`}</Text>
                </Flex>
              </Flex>
            </Card>
          ) : (
            <Text style={{ fontStyle: 'italic', fontWeight: 300 }} fontSize={'12px'}>No Case Linked</Text>
          )}
        </Box>
      </Flex>

      <Flex flexDirection={'column'} overflowY={'auto'} mb={4}>
        <Flex justifyContent={'space-between'} alignItems={'center'} mb={2}>
          <Text fontWeight={500} fontSize={'12px'} >Summary</Text>
          <Button variant={'outline'} style={{ width: 'fit-content', height: 'fit-content', padding: '2px 8px' }} fontSize={'12px'} onClick={() => onGoToStep(2)}>edit</Button>
        </Flex>
        <Box ml={4}>
          {data.summary ? (
            <Text dangerouslySetInnerHTML={{ __html: data.summary }} fontSize={'12px'}></Text>
          ) : (
            <Text style={{ fontStyle: 'italic', fontWeight: 300 }} fontSize={'12px'}>No Summary</Text>
          )}
        </Box>
      </Flex>

      <Flex flexDirection={'column'} overflowY={'auto'} mb={4}>
        <Flex justifyContent={'space-between'} alignItems={'center'} mb={2}>
          <Text fontWeight={500} fontSize={'12px'}>Internal Notes</Text>
          <Button variant={'outline'} style={{ width: 'fit-content', height: 'fit-content', padding: '2px 8px' }} fontSize={'12px'} onClick={() => onGoToStep(3)}>edit</Button>
        </Flex>

        <Box ml={4}>
          {data.internalNotes ? (
            <Text dangerouslySetInnerHTML={{ __html: data.internalNotes }} fontSize={'12px'}></Text>
          ) : (
            <Text style={{ fontStyle: 'italic', fontWeight: 300 }} fontSize={'12px'}>No Internal Notes</Text>
          )}
        </Box>
      </Flex>

      <Flex flex={1} />
      <Flex justifyContent={'space-between'} alignItems={'center'}>
        <Button variant={'white'} style={{ width: 'fit-content', padding: '6px 18px', fontSize: '16px' }} onClick={() => goBack()} disabled={disabled}>Back</Button>
        <Button ml={3} variant={'primary'} style={{ width: 'fit-content', padding: '6px 18px', fontSize: '16px' }} onClick={() => save()} disabled={disabled}>Save Summary</Button>
      </Flex>
      {/* <Button type='button' variant={'outline'} style={{ width: 'fit-content', padding: '4px 16px' }} alignSelf={'center'} mt={4} onClick={save}></Button> */}
    </Flex>
  )
}

export const VisitSummaryInner: React.FC<IVisitSummaryProps> = ({ calendlyUri, athenaId, userId, initialData, onClose }) => {
  const [steps, setSteps] = useState<Array<IStepperStep>>([{
    title: 'Linked Course',
    isCompleted: !!initialData
  }, {
    title: 'Linked Patient Case',
    isCompleted: !!initialData
  }, {
    title: 'Summary',
    isCompleted: !!initialData
  }, {
    title: 'Internal Notes',
    isCompleted: !!initialData
  }, {
    title: 'Review',
    isCompleted: !!initialData
  }])

  const [currentStep, setCurrentStep] = useState(0)

  const [isSelectingCase, setIsSelectingCase] = useState(false)
  const [courses, setCourses] = useState<Array<{ value: string, label: string }>>([])
  const [initializing, setInitializing] = useState(true)
  const [saveSummary, { loading: loadingSave }] = useMutation<IUpsertHealthcoachVisitSummaryOutput, IUpsertHealthcoachVisitSummaryInput>(UPSERT_HEALTH_COACH_VISIT_SUMMARY, {
    refetchQueries: [{
      query: GET_HEALTH_COACH_VISIT_SUMMARIES,
      variables: {
        calendlyUri: calendlyUri
      }
    },
    { query: GET_HEALTH_COACH_VISIT_SUMMARIES_ALL, variables: { userId } }],
    awaitRefetchQueries: true,
    onCompleted: (e => {
      if (e.healthCoachUpsertVisitSummary.ok) {
        onClose()
      }
    })
  })
  const { data: dataPresetPlans, loading: loadingPresetPlans, error } = useQuery<IGetPresetPlansOutput, IGetPresetPlansInput>(GET_PRESET_PLANS)
  const { data: dataCases, loading: loadingCases } = useQuery<IGetPatientCasesOutput, IGetPatientCasesInput>(GET_PATIENT_CASES, {
    variables: {
      patientId: athenaId
    },
  })

  useEffect(() => {
    if (!!loadingPresetPlans || !!loadingCases) return

    if (dataPresetPlans && dataPresetPlans.getThyroidPrograms) {
      setCourses([{
        label: 'AIP',
        value: 'AIP'
      },
      ...dataPresetPlans.getThyroidPrograms.map(x => {
        return {
          label: x.name,
          value: x.id
        }
      })
      ])
    }

    setInitializing(false)
  }, [loadingPresetPlans, dataPresetPlans, loadingCases])

  const onNext = () => {
    const stps = JSON.parse(JSON.stringify(steps)) as Array<IStepperStep>
    stps[currentStep].isCompleted = true
    stps[currentStep + 1].viewed = true
    setSteps(stps)
    setCurrentStep(currentStep + 1)
  }

  const onBack = () => {
    setCurrentStep(currentStep - 1)
  }

  return (
    <Flex flexDirection={'column'} style={{ width: 'min(600px, 80vw)', maxHeight: '90vh', height: '90vh', paddingRight: '40px' }} overflowY={'hidden'}>
      <Flex
        mb={4} justifyContent={'center'} alignItems={'center'}>
        <Flex
          mr={3} style={{ padding: '8px', borderRadius: 8, backgroundColor: 'rgb(33, 150, 243)', alignItems: 'center', justifyContent: 'center' }}>

          <svg width="36" height="36" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M80.7613 17.26H80.7423C80.7433 17.211 80.7573 17.165 80.7573 17.115C80.7573 15.7246 80.2052 14.3912 79.2225 13.4076C78.2397 12.4241 76.9066 11.8711 75.5163 11.87C75.4763 11.87 75.4403 11.881 75.4003 11.882H60.8423V8.478C60.8423 7.597 60.1283 6.885 59.2493 6.883V6.875H40.8003V6.881C40.7853 6.881 40.7723 6.877 40.7573 6.877C39.8773 6.877 39.1633 7.59 39.1633 8.471L39.1663 8.501V11.881H24.5013L24.4813 11.879C23.0915 11.8811 21.7592 12.4342 20.7766 13.417C19.794 14.3999 19.2411 15.7322 19.2393 17.122C19.2393 17.169 19.2523 17.212 19.2533 17.259H19.2393V87.831H19.2423L19.2393 87.858C19.2395 88.5462 19.3754 89.2276 19.639 89.8633C19.9026 90.4991 20.2889 91.0766 20.7758 91.563C21.2627 92.0494 21.8406 92.4351 22.4765 92.6982C23.1125 92.9612 23.794 93.0964 24.4823 93.096C24.5603 93.096 24.6333 93.076 24.7113 93.073V93.094H75.2113C75.3093 93.099 75.4023 93.123 75.5013 93.123C76.8905 93.1227 78.2227 92.5708 79.2051 91.5886C80.1876 90.6064 80.7397 89.2742 80.7403 87.885C80.7403 87.866 80.7353 87.849 80.7353 87.83H80.7633V17.26H80.7613ZM73.5 85.5H26.5V20H34.1753V24.878C34.1753 23.5 34.1853 22.903 34.1753 23C34.1753 23.998 34.996 25 36 25H64C64.4807 24.9995 65.1601 24.8399 65.5 24.5C65.8399 24.1601 65.8287 23.4807 65.8293 23C65.8273 21.9045 65.8253 21.017 65.8293 23V20H73.5V85.5Z" fill="white" />
            <path d="M39.1501 41.1188C39.1501 40.4608 38.6131 39.9238 37.9521 39.9238C37.9211 39.9238 37.8941 39.9388 37.8641 39.9418V39.9238H35.3621C35.0445 39.9241 34.74 40.0502 34.5153 40.2746C34.2905 40.499 34.1639 40.8033 34.1631 41.1208V43.7178C34.1631 44.3738 34.7001 44.9108 35.3621 44.9108H37.8651V44.8928C37.8951 44.8948 37.9211 44.9108 37.9511 44.9108C38.6141 44.9108 39.1511 44.3738 39.1511 43.7178V41.1208L39.1501 41.1188ZM65.8311 41.1188C65.8311 40.4608 65.2941 39.9238 64.6331 39.9238H45.3371C45.0195 39.9241 44.715 40.0502 44.4903 40.2746C44.2655 40.499 44.1389 40.8033 44.1381 41.1208V43.7178C44.1381 44.3738 44.6751 44.9108 45.3371 44.9108H64.6321C65.2951 44.9108 65.8321 44.3738 65.8321 43.7178V41.1208L65.8311 41.1188ZM39.1501 51.0938C39.1501 50.4358 38.6131 49.8988 37.9521 49.8988C37.9211 49.8988 37.8941 49.9138 37.8641 49.9168V49.8988H35.3621C35.0445 49.8991 34.74 50.0252 34.5153 50.2496C34.2905 50.474 34.1639 50.7783 34.1631 51.0958V53.6928C34.1631 54.3488 34.7001 54.8858 35.3621 54.8858H37.8651V54.8678C37.8951 54.8698 37.9211 54.8858 37.9511 54.8858C38.6141 54.8858 39.1511 54.3488 39.1511 53.6928V51.0958L39.1501 51.0938ZM65.8311 51.0938C65.8311 50.4358 65.2941 49.8988 64.6331 49.8988H45.3371C45.0195 49.8991 44.715 50.0252 44.4903 50.2496C44.2655 50.474 44.1389 50.7783 44.1381 51.0958V53.6928C44.1381 54.3488 44.6751 54.8858 45.3371 54.8858H64.6321C65.2951 54.8858 65.8321 54.3488 65.8321 53.6928V51.0958L65.8311 51.0938ZM39.1501 61.0688C39.1501 60.4108 38.6131 59.8738 37.9521 59.8738C37.9211 59.8738 37.8941 59.8888 37.8641 59.8918V59.8738H35.3621C35.0445 59.8741 34.74 60.0002 34.5153 60.2246C34.2905 60.449 34.1639 60.7533 34.1631 61.0708V63.6678C34.1631 64.3238 34.7001 64.8608 35.3621 64.8608H37.8651V64.8428C37.8951 64.8448 37.9211 64.8608 37.9511 64.8608C38.6141 64.8608 39.1511 64.3238 39.1511 63.6678V61.0708L39.1501 61.0688ZM65.8311 61.0688C65.8311 60.4108 65.2941 59.8738 64.6331 59.8738H45.3371C45.0195 59.8741 44.715 60.0002 44.4903 60.2246C44.2655 60.449 44.1389 60.7533 44.1381 61.0708V63.6678C44.1381 64.3238 44.6751 64.8608 45.3371 64.8608H64.6321C65.2951 64.8608 65.8321 64.3238 65.8321 63.6678V61.0708L65.8311 61.0688Z" fill="white" />
          </svg>
        </Flex>
        <Heading>VISIT SUMMARY</Heading>
      </Flex>

      <Stepper currentIndex={currentStep} steps={steps} onChange={(i) => { setCurrentStep(i) }} />

      {!!loadingCases || !!loadingPresetPlans || !courses || courses.length < 1 ? (
        <Flex flex={1} justifyContent={'center'} alignItems={'center'}>
          <PleaseWait />
        </Flex>
      ) : (<Flex flex={1} flexDirection={'column'} overflowY={'auto'} style={{marginRight:'-40px', paddingRight: '20px'}}>
        <Formik
          initialValues={{
            selectedLinkedCourse: initialData && initialData.selectedLinkedCourse ? courses.find(x => x.value === initialData.selectedLinkedCourse) : null,
            selectedLinkedNote: initialData && initialData.selectedLinkedNote ? dataCases?.providerGetPatientCases?.find(x => x.hcFilledFormId === initialData.selectedLinkedNote) : null,
            summary: initialData && initialData.summary ? initialData.summary : '',
            internalNotes: initialData && initialData.internalNotes ? initialData.internalNotes : ''
          } as IValues}
          onSubmit={(
            values: IValues,
            { setSubmitting }: FormikHelpers<IValues>
          ) => {
            saveSummary({
              variables: {
                id: initialData && initialData.id ? initialData.id : undefined,
                calendlyUri,
                userId,
                courseId: values.selectedLinkedCourse ? values.selectedLinkedCourse.value : undefined,
                internalForm: values.selectedLinkedNote ? values.selectedLinkedNote.hcFilledFormId : undefined,
                summary: values.summary,
                internalNotes: values.internalNotes
              }
            })
          }}

        >
          {({ touched, errors, isValid, values, submitForm, setFieldValue, setFieldTouched }) => {
            return (
              <>
                {currentStep === 0 && (
                  <SelectLinkedCourse courses={courses} isLoading={loadingPresetPlans} selectedCourse={values.selectedLinkedCourse} onSelected={e => {
                    setFieldValue('selectedLinkedCourse', e)
                  }}
                    skip={() => {
                      onNext()
                    }} />
                )}

                {currentStep === 1 && (
                  <>
                    <Text fontSize={'20px'} fontWeight={500} mb={2}>Linked Patient Case</Text>
                    <Text fontWeight={300} mb={4} fontSize={'14px'}>Linking a patient case might be useful for future reference. <span style={{ textDecoration: 'underline' }}>It won't be visible to the patient</span></Text>
                    {!values.selectedLinkedNote ? (
                      <>
                        <Button variant={'primary'} alignSelf={'center'} onClick={() => setIsSelectingCase(true)}>Select</Button>
                        <Flex flex={1} />
                        <Flex justifyContent={'space-between'} alignItems={'center'}>
                          <Button ml={3} variant={'white'} style={{ width: 'fit-content', padding: '6px 18px', fontSize: '16px' }} onClick={() => onBack()}>Back</Button>
                          <Button ml={3} variant={'primary'} style={{ width: 'fit-content', padding: '6px 18px', fontSize: '16px' }} onClick={() => onNext()}>Skip</Button>
                        </Flex>
                      </>
                    ) : (
                      <Flex justifyContent={'center'} flexDirection={'column'} flex={1}>
                        {values.selectedLinkedNote.patientCaseId ? (
                          <>
                            <Flex justifyContent={'center'} alignItems={'center'}>
                              <Button ml={3} variant={'danger'} style={{ width: 'fit-content', padding: '3px 8px', fontSize: '12px' }} onClick={() => setFieldValue('selectedLinkedNote', null)}>Remove</Button>
                              <Button ml={3} variant={'outline'} style={{ width: 'fit-content', padding: '3px 8px', fontSize: '12px' }} onClick={() => setIsSelectingCase(true)}>Change</Button>
                            </Flex>

                            <PatientCaseWithParams id={athenaId} caseId={values.selectedLinkedNote.patientCaseId} isInModal modalContainerStyle={{paddingLeft:'0px', paddingRight:'0px'}}/>
                            <Flex flex={1} />
                            <Flex justifyContent={'space-between'} alignItems={'center'}>
                              <Button ml={3} variant={'white'} style={{ width: 'fit-content', padding: '6px 18px', fontSize: '16px' }} onClick={() => onBack()}>Back</Button>
                              <Button ml={3} variant={'primary'} style={{ width: 'fit-content', padding: '6px 18px', fontSize: '16px' }} onClick={() => onNext()}>Next</Button>

                            </Flex>
                          </>
                        ) : (
                          <>
                            <Card mb={3} style={{
                              height: 'fit-content',
                              flex: 'initial',
                              padding: '8px 16px',
                              background: '#628fcb'
                            }} >
                              <Flex paddingX={'12px'} alignItems={'center'}>
                                <Flex flexDirection={'column'}>
                                  <Text color='white' fontWeight={500} fontSize={'14px'}>{values.selectedLinkedNote.subject}</Text>

                                  <Flex>
                                    <Text color='white' fontWeight={300} mr={2} fontSize={'12px'}>{`${values.selectedLinkedNote.lastModifiedDate}, `}</Text>
                                    <Text color='white' fontWeight={300} fontSize={'12px'}>{`${(values.selectedLinkedNote.documentSource || '').toLowerCase()}`}</Text>
                                  </Flex>
                                </Flex>
                              </Flex>
                            </Card>

                            <Flex justifyContent={'center'} alignItems={'center'}>
                              <Button ml={3} variant={'danger'} style={{ width: 'fit-content', padding: '3px 8px', fontSize: '12px' }} onClick={() => setFieldValue('selectedLinkedNote', null)}>Remove</Button>
                              <Button ml={3} variant={'outline'} style={{ width: 'fit-content', padding: '3px 8px', fontSize: '12px' }} onClick={() => setIsSelectingCase(true)}>Change</Button>
                            </Flex>

                            <Flex flex={1} />
                            <Flex justifyContent={'space-between'} alignItems={'center'}>
                              <Button ml={3} variant={'white'} style={{ width: 'fit-content', padding: '6px 18px', fontSize: '16px' }} onClick={() => onBack()}>Back</Button>
                              <Button ml={3} variant={'primary'} style={{ width: 'fit-content', padding: '6px 18px', fontSize: '16px' }} onClick={() => onNext()}>Next</Button>

                            </Flex>
                          </>
                        )}
                      </Flex>
                    )}


                  </>
                )}


                {currentStep == 2 && (
                  <>
                    <Text fontSize={'20px'} fontWeight={500} mb={2}>Summary</Text>
                    <Text mb={4} fontSize={'14px'} fontWeight={300}><span style={{ color: 'orangered', fontWeight: 500 }}>Warning:</span> This is what the patient will see</Text>
                    <WellnessPlanTextEditor
                      name={'summary'}
                      values={values}
                      setFieldValue={setFieldValue}
                      touched={touched} errors={errors}
                      initialValue={values.summary}
                      containerStyle={{ flex: 1 }}
                    />
                    <Flex justifyContent={'space-between'} alignItems={'center'} mt={4}>
                      <Button ml={3} variant={'white'} style={{ width: 'fit-content', padding: '6px 18px', fontSize: '16px' }} onClick={() => onBack()}>Back</Button>
                      <Button ml={3} variant={'primary'} style={{ width: 'fit-content', padding: '6px 18px', fontSize: '16px' }} onClick={() => onNext()}>Next</Button>
                    </Flex>
                  </>
                )}

                {currentStep == 3 && (
                  <>
                    <Text fontSize={'20px'} fontWeight={500} mb={2}>Internal Notes</Text>
                    <Text fontWeight={300} mb={4} fontSize={'14px'}><span style={{ textDecoration: 'underline' }}>It won't be visible to the patient</span></Text>
                    <WellnessPlanTextEditor
                      name={'internalNotes'}
                      values={values}
                      setFieldValue={setFieldValue}
                      touched={touched} errors={errors}
                      initialValue={values.internalNotes}
                      containerStyle={{ flex: 1 }}
                    />
                    <Flex justifyContent={'space-between'} alignItems={'center'} mt={4}>
                      <Button ml={3} variant={'white'} style={{ width: 'fit-content', padding: '6px 18px', fontSize: '16px' }} onClick={() => onBack()}>Back</Button>
                      <Button ml={3} variant={'primary'} style={{ width: 'fit-content', padding: '6px 18px', fontSize: '16px' }} onClick={() => onNext()}>Next</Button>
                    </Flex>
                  </>
                )}

                {currentStep == 4 && (
                  <>
                    <Review data={values} goBack={() => onBack()} onGoToStep={e => setCurrentStep(e)} save={() => { submitForm() }} disabled={loadingSave} />
                  </>
                )}

                {!!isSelectingCase && (
                  <EnhancedModal isOpen={!!isSelectingCase} onClose={() => setIsSelectingCase(false)}>
                    <SelectLinkedNote cases={dataCases?.providerGetPatientCases || []} onChange={e => {
                      setFieldValue('selectedLinkedNote', e)
                      setIsSelectingCase(false)
                    }} selectedCase={values.selectedLinkedNote} onClose={() => setIsSelectingCase(false)} />
                  </EnhancedModal>
                )}
              </>
            )
          }}
        </Formik>

      </Flex>)}
    </Flex>
  )
}

export const VisitSummary: React.FC<IVisitSummaryProps> = ({ calendlyUri, athenaId, userId, onClose }) => {
  const { data: visitSummary, loading: loadingVisitSummary, error } = useQuery<IGetHealthCoachVisitSummariesForVisitOutput, IGetHealthCoachVisitSummariesForVisitInput>(GET_HEALTH_COACH_VISIT_SUMMARIES, {
    variables: {
      calendlyUri: calendlyUri
    }
  })
  if (!!loadingVisitSummary) {
    return (
      <PleaseWait />
    )
  }
  console.log({ visitSummary })
  return (
    <VisitSummaryInner onClose={onClose} calendlyUri={calendlyUri} athenaId={athenaId} userId={userId} initialData={visitSummary && visitSummary.healthCoachGetPatientVisitSummariesForVisit ? {
      id: visitSummary.healthCoachGetPatientVisitSummariesForVisit.id,
      selectedLinkedCourse: visitSummary.healthCoachGetPatientVisitSummariesForVisit.courseId,
      selectedLinkedNote: visitSummary.healthCoachGetPatientVisitSummariesForVisit.internalForm,
      summary: visitSummary.healthCoachGetPatientVisitSummariesForVisit.summary,
      internalNotes: visitSummary.healthCoachGetPatientVisitSummariesForVisit.internalNotes
    } : undefined} />
  )

}